import { useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import { isAccountsHead } from "@/components/GlobalSwitcher";
import { getAccountsSettings, saveAccountsSettings } from "@/lib/api";
import { errMsg } from "@/components/accounts/shared";

// Approval limits and the capex threshold, per currency. Only the head of accounts (or an admin) edits.
export default function AccountsSettings({ gyms = [] }) {
  const { user } = useAuth();
  const head = isAccountsHead(user);
  const [s, setS] = useState(null);
  useEffect(() => { getAccountsSettings().then(setS).catch((e) => toast.error(errMsg(e, "Could not load settings"))); }, []);
  if (!s) return <div className="py-16 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-zinc-500" /></div>;
  const currencies = Object.keys(s.limits);
  const setLimit = (cur, k, v) => setS({ ...s, limits: { ...s.limits, [cur]: { ...s.limits[cur], [k]: v } } });
  const save = async () => {
    try {
      const out = await saveAccountsSettings({
        limits: Object.fromEntries(currencies.map((c) => [c, { facility_head: Number(s.limits[c].facility_head), accounts: Number(s.limits[c].accounts) }])),
        capex_threshold: Object.fromEntries(Object.entries(s.capex_threshold).map(([c, v]) => [c, Number(v)])),
        entities: (s.entities || []).filter((e) => e.name.trim()),
        cash_lookback_days: Number(s.cash_lookback_days) || 14,
      });
      setS(out);
      toast.success("Settings saved");
    } catch (e) { toast.error(errMsg(e, "Could not save")); }
  };
  return (
    <div className="space-y-4 max-w-3xl" data-testid="accounts-settings">
      <div className="card-surface overflow-x-auto">
        <div className="px-4 py-3">
          <div className="text-sm font-semibold text-zinc-100">Approval limits (refunds and purchases)</div>
          <div className="text-[11px] text-zinc-500">Up to the first limit the gym's facility head can approve; up to the second the accounts team; above it only a master admin.</div>
        </div>
        <table className="w-full text-sm">
          <thead><tr className="text-left text-[10px] uppercase tracking-[0.16em] text-zinc-500 border-b border-[#1f1f1f]">
            <th className="px-3 py-2">Currency</th><th className="px-3 py-2">Facility head up to</th><th className="px-3 py-2">Accounts up to</th><th className="px-3 py-2">Capex from</th></tr></thead>
          <tbody>
            {currencies.map((c) => (
              <tr key={c} className="border-b border-[#1a1a1a] last:border-0">
                <td className="px-3 py-2 text-zinc-300">{c}</td>
                <td className="px-3 py-2"><Input type="number" disabled={!head} value={s.limits[c].facility_head} onChange={(e) => setLimit(c, "facility_head", e.target.value)} className="bg-[#0a0a0a] border-[#262626] h-8 w-32" /></td>
                <td className="px-3 py-2"><Input type="number" disabled={!head} value={s.limits[c].accounts} onChange={(e) => setLimit(c, "accounts", e.target.value)} className="bg-[#0a0a0a] border-[#262626] h-8 w-32" /></td>
                <td className="px-3 py-2"><Input type="number" disabled={!head} value={s.capex_threshold[c] ?? ""} onChange={(e) => setS({ ...s, capex_threshold: { ...s.capex_threshold, [c]: e.target.value } })} className="bg-[#0a0a0a] border-[#262626] h-8 w-32" /></td>
              </tr>
            ))}
          </tbody>
        </table>
        {head && <div className="px-4 py-3 border-t border-[#1a1a1a]"><Button size="sm" onClick={save} className="bg-blue-600 hover:bg-blue-500" data-testid="settings-save">Save</Button></div>}
      </div>
      <div className="card-surface px-4 py-3 flex flex-wrap items-center gap-3 text-sm">
        <span className="text-zinc-200">Cash deposits match cash taken up to</span>
        <Input type="number" min={1} max={60} disabled={!head} value={s.cash_lookback_days ?? 14} onChange={(e) => setS({ ...s, cash_lookback_days: e.target.value })} className="bg-[#0a0a0a] border-[#262626] h-8 w-20" data-testid="settings-cash-lookback" />
        <span className="text-zinc-400">days before the deposit</span>
        {head && <Button size="sm" onClick={save} className="bg-blue-600 hover:bg-blue-500 h-8">Save</Button>}
      </div>
      <p className="text-[11px] text-zinc-500">A paid purchase at or over the capex amount is posted to Capex (depreciated monthly) instead of Expenses.</p>
      <div className="card-surface p-4 space-y-3" data-testid="settings-entities">
        <div>
          <div className="text-sm font-semibold text-zinc-100">Legal entities (GST / VAT registrations)</div>
          <div className="text-[11px] text-zinc-500">One per GSTIN / TRN / VAT number, with the gyms it covers. The Tax tab and the CA pack are per entity.</div>
        </div>
        {(s.entities || []).map((e, i) => {
          const set = (k, v) => setS({ ...s, entities: s.entities.map((x, j) => (j === i ? { ...x, [k]: v } : x)) });
          return (
            <div key={e.id || i} className="border border-[#1f1f1f] rounded-lg p-3 space-y-2">
              <div className="grid sm:grid-cols-5 gap-2">
                <Input disabled={!head} value={e.name} onChange={(ev) => set("name", ev.target.value)} placeholder="Legal name" className="sm:col-span-2 h-8 bg-[#0a0a0a] border-[#262626]" />
                <Input disabled={!head} value={e.country} onChange={(ev) => set("country", ev.target.value)} placeholder="Country" className="h-8 bg-[#0a0a0a] border-[#262626]" />
                <Input disabled={!head} value={e.tax_id} onChange={(ev) => set("tax_id", ev.target.value)} placeholder="GSTIN / TRN / VAT no." className="h-8 bg-[#0a0a0a] border-[#262626]" />
                <Input disabled={!head} value={e.state} onChange={(ev) => set("state", ev.target.value)} placeholder="State" className="h-8 bg-[#0a0a0a] border-[#262626]" />
              </div>
              <div className="flex flex-wrap items-center gap-1.5">
                {gyms.map((g) => (
                  <button key={g.id} type="button" disabled={!head} onClick={() => set("facility_ids", e.facility_ids.includes(g.id) ? e.facility_ids.filter((x) => x !== g.id) : [...e.facility_ids, g.id])}
                    className={`text-[11px] px-2 py-0.5 rounded-full border ${e.facility_ids.includes(g.id) ? "border-blue-500/60 bg-blue-500/10 text-blue-200" : "border-[#262626] text-zinc-500"}`}>{g.name}</button>
                ))}
                {head && <button onClick={() => setS({ ...s, entities: s.entities.filter((_, j) => j !== i) })} className="ml-auto text-zinc-500 hover:text-rose-300" aria-label="Remove entity"><Trash2 className="h-3.5 w-3.5" /></button>}
              </div>
            </div>
          );
        })}
        {head && <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={() => setS({ ...s, entities: [...(s.entities || []), { name: "", country: "India", tax_name: "GST", tax_id: "", state: "", facility_ids: [] }] })}
            className="h-8 border-[#262626] bg-transparent"><Plus className="h-3.5 w-3.5 mr-1" /> Entity</Button>
          <Button size="sm" onClick={save} className="bg-blue-600 hover:bg-blue-500 h-8">Save</Button></div>}
      </div>
    </div>
  );
}
